import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  TextField,
  Typography,
  useDeviceType,
  AccordionDetails,
  AccordionSummary,
  Notification,
  Grid,
  Accordion,
} from '@packages/shared';
import { ArrowMiniDown32 } from '@packages/themes/icons';
import { useCustomer } from '@packages/utilities';
import type { CaptchaData } from '@packages/captcha';
import type {
  GTMEventGlycerinBaseClick,
  GTMEventGlycerinDisplayNewsletterConfirm,
} from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { ActivityOverlay } from '../ActivityOverlay';
import { handleSubmit } from '../GeneralForm/helpers/handleSubmit';
import { prefillForm } from '../helpers/customerValue';
import { formatErrorMsg } from '../helpers/formatErrorMsg';
import { newsletterSubscribeFormSchema } from '../helpers/validationSchema';
import { basicContainer } from '../styles';
import type { ErrorType, FormValues, InputID } from '../types';
import { InputIDs } from '../types';
import { convertFormDataToPostData, initialFieldValues, toPrefill } from './helpers/settings';
import { subscribeSheetMessages } from './helpers/messages';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { CaptchaDialog } from '../CaptchaDialog';

const TermsAccordion = () => {
  const intl = useIntl();
  const { nlTexts } = useNlSheetTexts();
  return (
    <Accordion
      elevation={0}
      sx={{
        '&::before': { display: 'none' },
        typography: 'body2',
        backgroundColor: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowMiniDown32 />}
        aria-controls="newslettersheet-terms"
        id="newslettersheet-terms"
        sx={{
          padding: 0,
          justifyContent: 'left',
          '&.Mui-expanded': { minHeight: 'unset' },
          '.MuiAccordionSummary-content': { flexGrow: 'unset', margin: 0 },
        }}
      >
        {(nlTexts && nlTexts.field_voucher_conditions_id?.label) ||
          intl.formatMessage(subscribeSheetMessages.termsHeadline)}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Box
          sx={{ mt: 1, mb: 2, typography: 'body2', a: { color: 'text.dark' } }}
          dangerouslySetInnerHTML={{
            __html:
              (nlTexts && nlTexts.field_voucher_conditions_id?.field_conditions.processed) ||
              intl.formatMessage(subscribeSheetMessages.termsContent),
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * A compact Newsletter Subscribe Form with a simple success page after successful submit
 */
const Page1Form = ({ onSuccess }: { onSuccess: () => void }) => {
  const intl = useIntl();
  const { nlTexts } = useNlSheetTexts();
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();
  const { isMobile } = useDeviceType();
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [usedCaptchaData, setUsedCaptchaData] = useState<CaptchaData | undefined>(undefined);
  const [formData, setFormData] = useState<FormValues | undefined>(undefined);
  const handleCaptcha = (captchaData: CaptchaData) => {
    setUsedCaptchaData(captchaData);
  };
  const onSubmit = (formValues: FormValues) => {
    setFormData(formValues);
    return handleSubmit(
      () => {
        onSuccess();
        dispatchGTMEvent({
          event: 'ga_event',
          eventCategory: 'User Interactions',
          eventLabel: 'newsletter',
          eventAction: 'subscribe',
        });
        if (formValues?.email) {
          dispatchGTMEvent<GTMEventGlycerinDisplayNewsletterConfirm>({
            event: 'DisplayNewsletterConfirm',
            DisplayNewsletterConfirmData: {
              email: formValues?.email,
              custom: { areaKey: 'slideinPromotionFooter' },
            },
          });
        }
      },
      (message) => setNotification({ message, show: true }),
      intl,
      convertFormDataToPostData(formValues),
      'subscribe',
      isMobile ? 'mob' : 'web',
      handleCaptcha,
    );
  };

  // state of the whole form
  const {
    values,
    errors,
    setFieldValue,
    isValid,
    getFieldProps,
    dirty,
    touched,
    isSubmitting,
    submitForm,
  } = useFormik({
    initialValues: initialFieldValues,
    validationSchema: newsletterSubscribeFormSchema,

    onSubmit,
  });

  // the form is valid when all fields are valid and the form has been changed (i. e. it is "dirty")
  const formIsValid = isValid && dirty;
  const captchaSuccess = () => {
    if (formData) {
      onSubmit(formData).then(() => {});
    }
  };
  // If we are logged in as a customer, we prefill the formfields with the existing data
  useEffect(() => {
    prefillForm(toPrefill, customer, values, (value, id) => {
      setFieldValue(id, value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const basicPropsFor = (inputId: InputID) => ({
    ...getFieldProps(inputId),
    id: inputId,
    helperText:
      touched[inputId] && errors[inputId]
        ? formatErrorMsg(errors[inputId] as ErrorType, inputId, intl)
        : '',
    error: !!errors[inputId] && !!touched[inputId],
  });

  const buttonText =
    (nlTexts && (isMobile ? nlTexts.field_button_label_mobile : nlTexts.field_button_label)) ||
    intl.formatMessage(
      isMobile ? subscribeSheetMessages.buttonMobile : subscribeSheetMessages.button,
    );

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <Notification
        open={notification.show}
        severity="error"
        autoHideDuration={3500}
        onClose={() => setNotification({ ...notification, show: false })}
      >
        {notification.message}
      </Notification>
      {usedCaptchaData !== undefined && (
        <CaptchaDialog captchaData={usedCaptchaData} captchaSuccess={captchaSuccess} />
      )}
      <Box sx={{ ...basicContainer, padding: 2 }}>
        <Typography variant="h5" component="p" sx={{ marginBottom: 2.25 }}>
          {(nlTexts && nlTexts.field_headline) ||
            intl.formatMessage(subscribeSheetMessages.formHeadline)}
        </Typography>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitForm();
          }}
        >
          <Grid container columns={[1, 2]} columnSpacing={1.5}>
            <Grid item xs={1}>
              <TextField
                {...basicPropsFor(InputIDs.EMAIL)}
                sx={{ paddingTop: 0 }}
                fullWidth
                placeholder={intl.formatMessage(subscribeSheetMessages.emailPlaceholder)}
              />
            </Grid>
            <Grid item xs={1}>
              <Button
                disabled={!formIsValid}
                size="large"
                fullWidth
                color="primary"
                type="submit"
                onClick={() => {
                  dispatchGTMEvent<GTMEventGlycerinBaseClick>({
                    event: 'BaseClick',
                    BaseClickData: {
                      category: 'newsletter-sheet',
                      label: 'submitbutton',
                    },
                  });
                }}
              >
                {buttonText}
              </Button>
            </Grid>
          </Grid>

          <Box
            sx={{ mt: 1, mb: 2, typography: 'body2', a: { color: 'text.dark' } }}
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(subscribeSheetMessages.formDescription),
            }}
          />

          <TermsAccordion />
        </form>
      </Box>
    </ActivityOverlay>
  );
};

export const NewsletterSubscribeCompact = () => {
  const intl = useIntl();
  const [page, setPage] = useState(1);

  return page === 1 ? (
    <Page1Form onSuccess={() => setPage(2)} />
  ) : (
    <Box sx={{ ...basicContainer, padding: 2 }}>
      <Typography variant="h3" component="p" sx={{ marginBottom: 2.25 }}>
        {intl.formatMessage(subscribeSheetMessages.successHeadline)}
      </Typography>
      <Box sx={{ paddingBottom: 2.25, typography: 'body2' }}>
        {intl.formatMessage(subscribeSheetMessages.successContent)}
      </Box>
    </Box>
  );
};
